import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, Tag, ArrowRight } from 'lucide-react';
import Header from './components/Header4';
import Footer from './components/Footer4';
import Button from './components/Button';

const Cart = () => {
    const navigate = useNavigate();
    const [couponCode, setCouponCode] = useState('');
    const [appliedCoupon, setAppliedCoupon] = useState(null);

    const [cartItems, setCartItems] = useState([
        {
            id: 1,
            name: 'Banarasi Silk Saree',
            price: 12999,
            quantity: 1,
            size: 'Free Size',
            image: 'https://images.pexels.com/photos/2955819/pexels-photo-2955819.jpeg?auto=compress&cs=tinysrgb&w=400',
        },
        {
            id: 2,
            name: 'Designer Lehenga Set',
            price: 24500,
            quantity: 1,
            size: 'M',
            image: 'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=400',
        },
    ]);

    const updateQuantity = (id, delta) => {
        setCartItems(cartItems.map(item =>
            item.id === id ? { ...item, quantity: Math.max(1, item.quantity + delta) } : item
        ));
    };

    const removeItem = (id) => {
        setCartItems(cartItems.filter(item => item.id !== id));
    };

    const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const shipping = subtotal > 5000 ? 0 : 199;
    const discount = appliedCoupon ? Math.round(subtotal * 0.1) : 0;
    const total = subtotal + shipping - discount;

    return (
        <div className="w-full min-h-screen bg-white" style={{ fontFamily: "'Work Sans', sans-serif" }}>
            <Header cartCount={cartItems.length} />
            <link href="https://fonts.googleapis.com/css2?family=Work+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

            <main className="pt-20 md:pt-24 pb-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h1 className="text-2xl md:text-3xl font-semibold text-[#1B4965] mb-8 pt-4">Shopping Cart</h1>

                    {cartItems.length === 0 ? (
                        <div className="text-center py-20">
                            <p className="text-lg text-[#6B7280] mb-6">Your cart is empty</p>
                            <Button variant="primary" onClick={() => navigate('/template4/shop')}>
                                Continue Shopping
                            </Button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                            {/* Cart Items */}
                            <div className="lg:col-span-2 space-y-4">
                                {cartItems.map((item) => (
                                    <div key={item.id} className="flex gap-4 p-4 border border-[#E5E7EB] rounded-sm">
                                        <img
                                            src={item.image}
                                            alt={item.name}
                                            className="w-20 h-24 sm:w-24 sm:h-32 object-cover rounded-sm cursor-pointer"
                                            onClick={() => navigate(`/template4/product/${item.id}`)}
                                        />
                                        <div className="flex-1">
                                            <h3 className="text-sm sm:text-base font-medium text-[#1A1A1A] mb-1">{item.name}</h3>
                                            <p className="text-xs text-[#6B7280] mb-3">Size: {item.size}</p>

                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center border border-[#E5E7EB] rounded-sm">
                                                    <button
                                                        onClick={() => updateQuantity(item.id, -1)}
                                                        className="w-8 h-8 flex items-center justify-center text-[#6B7280] hover:text-[#1B4965]"
                                                    >
                                                        <Minus className="w-3 h-3" />
                                                    </button>
                                                    <span className="w-8 h-8 flex items-center justify-center text-sm font-medium text-[#1A1A1A] border-x border-[#E5E7EB]">
                                                        {item.quantity}
                                                    </span>
                                                    <button
                                                        onClick={() => updateQuantity(item.id, 1)}
                                                        className="w-8 h-8 flex items-center justify-center text-[#6B7280] hover:text-[#1B4965]"
                                                    >
                                                        <Plus className="w-3 h-3" />
                                                    </button>
                                                </div>

                                                <div className="flex items-center gap-4">
                                                    <span className="text-sm font-semibold text-[#1B4965]">
                                                        {'\u20B9'}{(item.price * item.quantity).toLocaleString()}
                                                    </span>
                                                    <button
                                                        onClick={() => removeItem(item.id)}
                                                        className="text-[#6B7280] hover:text-red-500 transition-colors"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Order Summary */}
                            <div className="lg:col-span-1">
                                <div className="bg-[#F0F7FA] p-6 rounded-sm sticky top-24">
                                    <h2 className="text-lg font-semibold text-[#1B4965] mb-6">Order Summary</h2>

                                    {/* Coupon */}
                                    <div className="flex gap-2 mb-6">
                                        <div className="relative flex-1">
                                            <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
                                            <input
                                                type="text"
                                                placeholder="Coupon code"
                                                value={couponCode}
                                                onChange={(e) => setCouponCode(e.target.value)}
                                                className="w-full pl-9 pr-3 py-2.5 text-sm border border-[#E5E7EB] rounded-sm focus:outline-none focus:border-[#1B4965]"
                                            />
                                        </div>
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => {
                                                if (couponCode) setAppliedCoupon(couponCode);
                                            }}
                                        >
                                            Apply
                                        </Button>
                                    </div>

                                    <div className="space-y-3 mb-6">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-[#6B7280]">Subtotal</span>
                                            <span className="text-[#1A1A1A] font-medium">{'\u20B9'}{subtotal.toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-[#6B7280]">Shipping</span>
                                            <span className="text-[#1A1A1A] font-medium">
                                                {shipping === 0 ? 'Free' : `\u20B9${shipping}`}
                                            </span>
                                        </div>
                                        {discount > 0 && (
                                            <div className="flex justify-between text-sm">
                                                <span className="text-[#62B6CB]">Discount</span>
                                                <span className="text-[#62B6CB] font-medium">-{'\u20B9'}{discount.toLocaleString()}</span>
                                            </div>
                                        )}
                                        <div className="pt-3 border-t border-[#E5E7EB] flex justify-between">
                                            <span className="font-semibold text-[#1A1A1A]">Total</span>
                                            <span className="text-lg font-bold text-[#1B4965]">{'\u20B9'}{total.toLocaleString()}</span>
                                        </div>
                                    </div>

                                    <Button
                                        variant="primary"
                                        size="lg"
                                        fullWidth
                                        onClick={() => navigate('/template4/checkout')}
                                        className="gap-2"
                                    >
                                        Checkout <ArrowRight className="w-4 h-4" />
                                    </Button>

                                    <button
                                        onClick={() => navigate('/template4/shop')}
                                        className="w-full mt-3 text-sm text-[#1B4965] hover:text-[#133B53] font-medium text-center bg-transparent border-none cursor-pointer"
                                    >
                                        Continue Shopping
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default Cart;
